import { Block, Color, Shimmer, Text } from '@snowflake/core-ui';
import { ListingInformation } from '@signup/shared';
import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import * as uuid from 'uuid';
import { useSignupPageContext } from '../../pages/SignupContext';
import { MarketingTextType } from '../../types';
import { isDefaultOrCortexCodeCLIFlow } from '../../utils/signupFlowTypeUtils';
import { useSignupFlowType } from '../../utils/useSignupFlowType';

export const InfoListItem = (props: { children: React.ReactNode }) => {
  const signupFlowType = useSignupFlowType();

  const marginBottom = isDefaultOrCortexCodeCLIFlow(signupFlowType) ? 16 : 12;

  return (
    <Block
      display="flex"
      justifyContent="space-between"
      verticalAlign="top"
      marginBottom={marginBottom}
    >
      <Block marginTop="6px" marginRight={16}>
        <img src="/static/images/rectangle-bullet.svg" />
      </Block>
      {props.children}
    </Block>
  );
};

export const InfoText = (props: { children: React.ReactNode }) => {
  const signupFlowType = useSignupFlowType();
  const textColor = isDefaultOrCortexCodeCLIFlow(signupFlowType) ? ('#5D6A85' as any) : Color.White;

  const marginBottom = isDefaultOrCortexCodeCLIFlow(signupFlowType) ? 0 : 12;

  return (
    <Text color={textColor} size="large" lineHeight="30px" marginBottom={marginBottom} width="100%">
      {props.children}
    </Text>
  );
};

export interface InfoListProps {
  listingInformation?: ListingInformation;
  isLoading?: boolean;
}

export const InfoList = (props: InfoListProps) => {
  const { renderingOptions } = useSignupPageContext();
  const signupFlowType = useSignupFlowType();
  const complianceImageVariant = isDefaultOrCortexCodeCLIFlow(signupFlowType) ? 'black' : 'white';

  const getLoadingOrListingMarketingText = React.useCallback(() => {
    return [
      <Block key="1" display="flex" flexWrap="wrap">
        <FormattedMessage
          id="Receive real time updates from <providerName></providerName> automatically"
          description="Indicates who provides updates for a package. Contains a blank for the author's name."
          values={{
            providerName: function ProviderName() {
              if (props.isLoading) {
                return (
                  <Shimmer
                    role="progressbar"
                    aria-busy={true}
                    height={20}
                    width="15%"
                    marginTop={4}
                    marginLeft={4}
                    marginRight={4}
                  />
                );
              } else {
                return props.listingInformation?.providerName;
              }
            },
          }}
        />
      </Block>,
      <FormattedMessage key="2" id="Explore data, services and more without ETL" />,
      <FormattedMessage
        key="3"
        id="Scale your critical data workloads instantly, elastically, and near-infinitely across public clouds"
      />,
    ];
  }, [props.isLoading, props.listingInformation]);

  const marketingText = React.useMemo(() => {
    if (renderingOptions.marketingTextType === MarketingTextType.LISTING) {
      return getLoadingOrListingMarketingText();
    } else if (renderingOptions.marketingTextType === MarketingTextType.BASIC) {
      return renderingOptions.marketingInfoList;
    } else {
      return [];
    }
  }, [
    renderingOptions.marketingTextType,
    renderingOptions.marketingInfoList,
    getLoadingOrListingMarketingText,
  ]);

  const bulletedMarketingText = marketingText.map(text => {
    return (
      <InfoListItem key={uuid.v4()}>
        <InfoText>{text}</InfoText>
      </InfoListItem>
    );
  });

  return (
    <Block className="trial-info-list" marginBottom={24}>
      {bulletedMarketingText}
      {renderingOptions.additionalMarketingContent}
      {!renderingOptions.hideCertifications && (
        <InfoListItem>
          <Block display="flex" flexDirection="column">
            <InfoText>
              <FormattedMessage id="Snowflake is HIPAA, PCI DSS, SOC 1 and SOC 2 Type 2 compliant, and FedRAMP Authorized" />
            </InfoText>
            <Block
              className="trial-info-list-certification-icons"
              display="flex"
              flexDirection="row"
              style={{ gap: '40px' }}
              marginTop="32px"
            >
              <img
                src={`/static/images/compliance/${complianceImageVariant}/hipaa.png`}
                height="40px"
                alt="HIPAA"
              />
              <img
                src={`/static/images/compliance/${complianceImageVariant}/pci.png`}
                height="40px"
                alt="PCI"
              />
              <img
                src={`/static/images/compliance/${complianceImageVariant}/soc2.png`}
                height="40px"
                alt="SOC 2"
              />
              <img
                src={`/static/images/compliance/${complianceImageVariant}/fedramp.png`}
                height="40px"
                alt="FedRAMP"
              />
            </Block>
          </Block>
        </InfoListItem>
      )}
    </Block>
  );
};
